import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import { AuthPlatform, StoredTokens } from '../types';
import VKIconSvg from '../assets/vk.svg';
import AdminPanel from './AdminPanel';
import MaxIcon from './MaxIcon';
import { MAX_VK_ACCOUNTS, vkAccountUid, type StoredVkAccount } from '../utils/vkAccounts';
import { getSkin } from '../utils/skin';
import { SkinLegal, SkinMark } from './SkinBrand';

/**
 * `?only=` — жёсткий фильтр способов входа: один способ (Telegram Boss шлёт `only=telegram`)
 * или несколько через запятую (socpower — `only=vk,email`: ВКонтакте и почта).
 *
 * Принимаем разумные синонимы: сервисы уже писали `only=tg`, и такое значение
 * раньше не совпадало ни с чем — экран оставался вообще без кнопок входа.
 * Неизвестные значения осознанно пропускаем по той же причине; если не узнали
 * ни одного — показываем все способы, а не пустой экран.
 */
const ONLY_ALIASES: Record<string, AuthPlatform> = {
  vk: 'vk',
  vkontakte: 'vk',
  vkid: 'vk',
  telegram: 'telegram',
  tg: 'telegram',
  max: 'max',
  yandex: 'yandex',
  ya: 'yandex',
  'yandex-id': 'yandex',
  email: 'email',
  mail: 'email',
};

const parseOnlyParam = (raw: string | null): AuthPlatform[] | null => {
  if (!raw) return null;
  const list = raw
    .split(',')
    .map((part) => ONLY_ALIASES[part.trim().toLowerCase()])
    .filter((platform): platform is AuthPlatform => Boolean(platform));
  return list.length ? Array.from(new Set(list)) : null;
};

interface AuthSelectorProps {
  onSelectVK: () => void;
  onSelectTelegram: () => void;
  onSelectMax: () => void;
  onSelectYandex: () => void;
  onSelectEmail: () => void;
  storedTokensData?: StoredTokens;
  /** true, пока идёт опрос бэка по сохранённым сессиям (для инлайн-индикатора). */
  isVerifyingSessions?: boolean;
  onQuickLogin?: (platform: AuthPlatform) => Promise<void>;
  onClearStoredData?: (platform?: AuthPlatform) => void;
  onConnectTelegram?: () => void;
  /** Сохранённые VK-аккаунты для быстрого переключения. */
  vkAccounts?: StoredVkAccount[];
  /** «Добавить аккаунт ВКонтакте» — запускает новый вход VK. */
  onAddVkAccount?: () => void;
  /** Переключиться на сохранённый VK-аккаунт. */
  onSwitchVkAccount?: (acc: StoredVkAccount) => void;
  /** Удалить сохранённый VK-аккаунт из списка. */
  onRemoveVkAccount?: (acc: StoredVkAccount) => void;
}

const AuthSelector: React.FC<AuthSelectorProps> = ({
  onSelectVK,
  onSelectTelegram,
  onSelectMax,
  onSelectYandex,
  onSelectEmail,
  storedTokensData = {},
  isVerifyingSessions = false,
  onQuickLogin,
  onClearStoredData,
  onConnectTelegram,
  vkAccounts = [],
  onAddVkAccount,
  onSwitchVkAccount,
  onRemoveVkAccount
}) => {
  const { state } = useAuth();
  const skin = getSkin();

  // Получаем query параметры из URL
  const queryParams = React.useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return {
      // Со скином сервиса его способы входа держатся и без `?only=` (после возврата с id.vk.ru адрес голый).
      only: parseOnlyParam(params.get('only') ?? skin?.only ?? null),
      service: params.get('service')
    };
  }, [skin]);

  // Определяем, нужен ли Telegram-first режим (для Telegram Boss)
  const isTelegramBossMode = queryParams.service === 'telegram-boss';

  // Проверяем, является ли пользователь админом
  const isAdmin = React.useMemo(
    () => Object.values(storedTokensData).some((status) => status?.backendUser?.role === 'admin'),
    [storedTokensData],
  );

  const hasStoredSession = Boolean(
    storedTokensData?.vk?.user
    || storedTokensData?.telegram?.user
    || storedTokensData?.max?.user
    || storedTokensData?.yandex?.user
    || storedTokensData?.email?.user,
  );

  // Полноэкранный лоадер показываем только когда показать нечего (нет кэша сессий).
  // Если сохранённые аккаунты есть — рендерим их сразу, а факт проверки на бэке
  // показываем ненавязчивым инлайн-индикатором (см. ниже), чтобы не создавалось
  // ощущение, будто аккаунты «слетели» на время загрузки.
  if (state.isLoading && !hasStoredSession) {
    return (
      <div className="flex flex-col justify-center items-center px-6 min-h-screen bg-zinc-900">
        <div className="w-8 h-8 rounded-full border-2 animate-spin border-zinc-700 border-t-white"></div>
        <p className="mt-4 text-sm text-zinc-500">Загрузка...</p>
      </div>
    );
  }

  const storedVKUser = storedTokensData?.vk?.user;
  const storedTelegramUser = storedTokensData?.telegram?.user;
  const storedMaxUser = storedTokensData?.max?.user;
  const storedYandexUser = storedTokensData?.yandex?.user;
  const storedEmailUser = storedTokensData?.email?.user;
  const isVKExpired = storedTokensData?.vk?.isExpired;
  const isTelegramExpired = storedTokensData?.telegram?.isExpired;
  const isMaxExpired = storedTokensData?.max?.isExpired;
  const isYandexExpired = storedTokensData?.yandex?.isExpired;
  const isEmailExpired = storedTokensData?.email?.isExpired;
  const isVKUnverified = storedTokensData?.vk?.isUnverified;
  const isTelegramUnverified = storedTokensData?.telegram?.isUnverified;
  const isMaxUnverified = storedTokensData?.max?.isUnverified;
  const isYandexUnverified = storedTokensData?.yandex?.isUnverified;
  const isEmailUnverified = storedTokensData?.email?.isUnverified;

  // Активный VK-аккаунт и прочие сохранённые (для быстрого переключения).
  const activeVkUid = storedTokensData?.vk
    ? vkAccountUid(storedTokensData.vk.backendUser, storedTokensData.vk.user)
    : null;
  const otherVkAccounts = vkAccounts.filter((a) => a.uid !== activeVkUid);
  const canAddVkAccount = vkAccounts.length < MAX_VK_ACCOUNTS;

  const vkConnection = storedTokensData?.vk?.backendUser?.connection;
  const telegramConnection = storedTokensData?.telegram?.backendUser?.connection;
  const hasUnifiedAccount = (vkConnection?.vk && vkConnection?.telegram) || 
                           (telegramConnection?.vk && telegramConnection?.telegram);
  
  // `only` — жёсткий фильтр. Раньше его перебивали hasUnifiedAccount и сохранённые
  // сессии, и сервис, попросивший только Telegram, всё равно видел ВКонтакте.
  // Если сервис назвал способы входа — показываем ровно их.
  const { only } = queryParams;
  const allowed = (platform: AuthPlatform) => (only ? only.includes(platform) : true);
  const shouldShowVK = allowed('vk');
  const shouldShowTelegram = allowed('telegram');
  const shouldShowMax = allowed('max');
  // Яндекс — не отдельный способ входа, а один из вариантов внутри «Почты» (чтобы не плодить
  // кнопки). Отдельной строкой показываем только сохранённую сессию или явный deep-link.
  const shouldShowYandex = only ? only.includes('yandex') : Boolean(storedYandexUser);
  const shouldShowEmail = allowed('email');

  const handleVKClick = () => {
    if (storedVKUser && onQuickLogin && !isVKExpired) {
      onQuickLogin('vk');
    } else {
      onSelectVK();
    }
  };

  const handleTelegramClick = () => {
    if (storedTelegramUser && onQuickLogin && !isTelegramExpired) {
      onQuickLogin('telegram');
    } else {
      onSelectTelegram();
    }
  };

  const handleMaxClick = () => {
    if (storedMaxUser && onQuickLogin && !isMaxExpired) {
      onQuickLogin('max');
    } else {
      onSelectMax();
    }
  };

  const handleYandexClick = () => {
    if (storedYandexUser && onQuickLogin && !isYandexExpired) {
      onQuickLogin('yandex');
    } else {
      onSelectYandex();
    }
  };

  const handleEmailClick = () => {
    if (storedEmailUser && onQuickLogin && !isEmailExpired) {
      onQuickLogin('email');
    } else {
      onSelectEmail();
    }
  };

  const handleClearVK = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClearStoredData?.('vk');
  };

  const handleClearTelegram = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClearStoredData?.('telegram');
  };

  const handleClearMax = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClearStoredData?.('max');
  };

  const handleClearYandex = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClearStoredData?.('yandex');
  };

  const handleClearEmail = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClearStoredData?.('email');
  };

  return (
    <div className="flex flex-col justify-between min-h-screen bg-zinc-900">
      {/* Header */}
      <div className="px-6 pt-8 pb-6 border-b border-zinc-800/50">
        <div className="flex justify-center items-center mb-2 space-x-3">
          {skin ? (
            <SkinMark />
          ) : (
            <div className="flex justify-center items-center w-10 h-10 rounded-xl bg-zinc-800">
              <svg className="w-5 h-5 text-zinc-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
          )}
        </div>
        <h1 className="text-xl font-semibold text-center text-white">
          {skin ? `Вход в ${skin.appName}` : 'Авторизация'}
        </h1>
        <p className="mt-1 text-sm text-center text-zinc-500">
          Выберите способ входа
        </p>
      </div>

      {/* Content */}
      <div className="flex-1 px-6 py-6">
        {/* Индикатор проверки сохранённых сессий — показываем поверх уже видимых
            аккаунтов, чтобы было понятно, что данные подгружаются, а не пропали. */}
        {isVerifyingSessions && hasStoredSession && (
          <div className="flex items-center justify-center gap-2 mb-4 text-xs text-zinc-500">
            <span className="w-3.5 h-3.5 rounded-full border-2 animate-spin border-zinc-700 border-t-zinc-400" />
            Проверяем сохранённые сессии…
          </div>
        )}

        {/* Telegram Boss Info Banner */}
        {isTelegramBossMode && (
          <div className="flex items-start p-3 mb-4 rounded-xl border bg-[#27A7E7]/10 border-[#27A7E7]/30">
            <svg className="flex-shrink-0 mt-0.5 mr-2.5 w-4 h-4 text-[#27A7E7]" fill="currentColor" viewBox="0 0 24 24">
              <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
            </svg>
            <div className="text-xs leading-relaxed text-[#27A7E7]/90">
              <span className="font-medium">Telegram Boss</span> работает только с Telegram-аккаунтами. Рекомендуем войти через Telegram для быстрого доступа.
            </div>
          </div>
        )}

        <div className="space-y-3">
          {/* Telegram Button - показываем первым в режиме Telegram Boss */}
          {isTelegramBossMode && shouldShowTelegram && (
            <div className="relative">
              <button
                onClick={handleTelegramClick}
                className={`w-full flex items-center px-4 py-3.5 rounded-xl transition-all duration-200 ${
                  isTelegramExpired 
                    ? 'bg-orange-500/10 border border-orange-500/30 hover:bg-orange-500/20' 
                    : 'bg-[#27A7E7]/10 border border-[#27A7E7]/30 hover:bg-[#27A7E7]/20'
                }`}
              >
                {storedTelegramUser ? (
                  <>
                    <div className="overflow-hidden flex-shrink-0 w-9 h-9 rounded-lg bg-zinc-800">
                      {storedTelegramUser.avatar ? (
                        <img src={storedTelegramUser.avatar} alt="" className="object-cover w-full h-full" />
                      ) : (
                        <div className="flex justify-center items-center w-full h-full font-medium text-zinc-400">
                          {storedTelegramUser.name.charAt(0)}
                        </div>
                      )}
                    </div>
                    <div className="flex-1 ml-3 min-w-0 text-left">
                      <div className="text-sm font-medium text-white truncate">
                        {storedTelegramUser.name}
                      </div>
                      <div className={`text-xs ${isTelegramExpired ? 'text-orange-400' : isTelegramUnverified ? 'text-amber-400/80' : 'text-zinc-500'}`}>
                        {isTelegramExpired ? 'Сессия истекла' : isTelegramUnverified ? 'Нет связи — данные из кэша' : 'Telegram'}
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="w-9 h-9 rounded-lg bg-[#27A7E7] flex items-center justify-center flex-shrink-0">
                      <svg className="w-5 h-5 text-on-accent" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
                      </svg>
                    </div>
                    <span className="ml-3 text-sm font-medium text-white">Telegram</span>
                    <span className="px-2 py-0.5 ml-2 text-xs font-medium rounded-full bg-[#27A7E7]/20 text-[#27A7E7]">
                      Рекомендуем
                    </span>
                  </>
                )}
                <svg className="ml-auto w-4 h-4 text-zinc-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
              
              {storedTelegramUser && !isTelegramExpired && (
                <button
                  onClick={handleClearTelegram}
                  className="flex absolute right-12 top-1/2 justify-center items-center w-7 h-7 rounded-lg transition-colors -translate-y-1/2 bg-zinc-800 hover:bg-red-500/20"
                  title="Выйти"
                >
                  <svg className="w-3.5 h-3.5 text-zinc-500 hover:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          )}

          {/* Разделитель для Telegram Boss режима */}
          {isTelegramBossMode && (shouldShowVK || shouldShowMax || shouldShowYandex || shouldShowEmail) && (
            <div className="relative py-3">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-zinc-800"></div>
              </div>
              <div className="relative flex justify-center">
                <span className="px-3 text-xs bg-zinc-900 text-zinc-500">
                  или войдите другим способом
                </span>
              </div>
            </div>
          )}

          {/* Подсказка для альтернативных методов в режиме Telegram Boss */}
          {isTelegramBossMode && (shouldShowVK || shouldShowMax || shouldShowYandex || shouldShowEmail) && (
            <p className="pb-2 text-xs text-center text-zinc-500">
              При входе другим способом вам нужно будет привязать Telegram
            </p>
          )}

          {/* VK Button */}
          {shouldShowVK && (
            <div className="relative">
              <button
                onClick={handleVKClick}
                className={`w-full flex items-center px-4 py-3.5 rounded-xl transition-all duration-200 ${
                  isVKExpired 
                    ? 'bg-orange-500/10 border border-orange-500/30 hover:bg-orange-500/20' 
                    : 'bg-[#0077FF]/10 border border-[#0077FF]/30 hover:bg-[#0077FF]/20'
                }`}
              >
                {storedVKUser ? (
                  <>
                    <div className="overflow-hidden flex-shrink-0 w-9 h-9 rounded-lg bg-zinc-800">
                      {storedVKUser.avatar ? (
                        <img src={storedVKUser.avatar} alt="" className="object-cover w-full h-full" />
                      ) : (
                        <div className="flex justify-center items-center w-full h-full font-medium text-zinc-400">
                          {storedVKUser.name.charAt(0)}
                        </div>
                      )}
                    </div>
                    <div className="flex-1 ml-3 min-w-0 text-left">
                      <div className="text-sm font-medium text-white truncate">
                        {storedVKUser.name}
                      </div>
                      <div className={`text-xs ${isVKExpired ? 'text-orange-400' : isVKUnverified ? 'text-amber-400/80' : 'text-zinc-500'}`}>
                        {isVKExpired ? 'Сессия истекла' : isVKUnverified ? 'Нет связи — данные из кэша' : hasUnifiedAccount ? 'VK + Telegram' : 'ВКонтакте'}
                      </div>
                    </div>
                    {hasUnifiedAccount && !isVKExpired && (
                      <div className="w-5 h-5 rounded-full bg-[#27A7E7]/20 flex items-center justify-center ml-2">
                        <svg className="w-3 h-3 text-[#27A7E7]" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
                        </svg>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <div className="w-9 h-9 rounded-lg bg-[#0077FF] flex items-center justify-center flex-shrink-0">
                      <img src={VKIconSvg} alt="VK" className="w-auto h-4" />
                    </div>
                    <span className="ml-3 text-sm font-medium text-white">ВКонтакте</span>
                  </>
                )}
                <svg className="ml-auto w-4 h-4 text-zinc-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
              
              {storedVKUser && !isVKExpired && (
                <button
                  onClick={handleClearVK}
                  className="flex absolute right-12 top-1/2 justify-center items-center w-7 h-7 rounded-lg transition-colors -translate-y-1/2 bg-zinc-800 hover:bg-red-500/20"
                  title="Выйти"
                >
                  <svg className="w-3.5 h-3.5 text-zinc-500 hover:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          )}

          {/* Прочие сохранённые VK-аккаунты + «Добавить аккаунт» (когда уже есть вход в VK) */}
          {shouldShowVK && storedVKUser && !isVKExpired && (
            <>
              {otherVkAccounts.map((acc) => (
                <div key={acc.uid} className="relative">
                  <button
                    onClick={() => onSwitchVkAccount?.(acc)}
                    className="w-full flex items-center px-4 py-3 rounded-xl border transition-all duration-200 bg-zinc-800/40 border-zinc-700/60 hover:bg-zinc-800"
                  >
                    <div className="overflow-hidden flex-shrink-0 w-9 h-9 rounded-lg bg-zinc-800">
                      {acc.user.avatar ? (
                        <img src={acc.user.avatar} alt="" className="object-cover w-full h-full" />
                      ) : (
                        <div className="flex justify-center items-center w-full h-full font-medium text-zinc-400">
                          {acc.user.name.charAt(0)}
                        </div>
                      )}
                    </div>
                    <div className="flex-1 ml-3 min-w-0 text-left">
                      <div className="text-sm font-medium text-white truncate">{acc.user.name}</div>
                      <div className="text-xs text-zinc-500">ВКонтакте</div>
                    </div>
                    <svg className="ml-auto w-4 h-4 text-zinc-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                  {onRemoveVkAccount && (
                    <button
                      onClick={(e) => { e.stopPropagation(); onRemoveVkAccount(acc); }}
                      className="flex absolute right-12 top-1/2 justify-center items-center w-7 h-7 rounded-lg transition-colors -translate-y-1/2 bg-zinc-800 hover:bg-red-500/20"
                      title="Удалить аккаунт"
                    >
                      <svg className="w-3.5 h-3.5 text-zinc-500 hover:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
              ))}

              {onAddVkAccount && canAddVkAccount && (
                <button
                  onClick={onAddVkAccount}
                  className="w-full flex items-center justify-center px-4 py-3 rounded-xl border border-dashed border-[#0077FF]/40 text-[#0077FF] text-sm font-medium hover:bg-[#0077FF]/10 transition-colors"
                >
                  <svg className="mr-2 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  {/* socpower говорит людям «страница» — не «аккаунт»: одно слово на всём пути входа. */}
                  {skin ? 'Добавить страницу ВКонтакте' : 'Добавить аккаунт ВКонтакте'}
                </button>
              )}

              {onAddVkAccount && !canAddVkAccount && (
                <p className="pt-1 text-xs text-center text-zinc-500">
                  Достигнут лимит в {MAX_VK_ACCOUNTS} аккаунтов
                </p>
              )}
            </>
          )}

          {/* Telegram Button - в обычном режиме (не Telegram Boss) */}
          {!isTelegramBossMode && shouldShowTelegram && (
            <div className="relative">
              <button
                onClick={handleTelegramClick}
                className={`w-full flex items-center px-4 py-3.5 rounded-xl transition-all duration-200 ${
                  isTelegramExpired 
                    ? 'bg-orange-500/10 border border-orange-500/30 hover:bg-orange-500/20' 
                    : 'bg-[#27A7E7]/10 border border-[#27A7E7]/30 hover:bg-[#27A7E7]/20'
                }`}
              >
                {storedTelegramUser ? (
                  <>
                    <div className="overflow-hidden flex-shrink-0 w-9 h-9 rounded-lg bg-zinc-800">
                      {storedTelegramUser.avatar ? (
                        <img src={storedTelegramUser.avatar} alt="" className="object-cover w-full h-full" />
                      ) : (
                        <div className="flex justify-center items-center w-full h-full font-medium text-zinc-400">
                          {storedTelegramUser.name.charAt(0)}
                        </div>
                      )}
                    </div>
                    <div className="flex-1 ml-3 min-w-0 text-left">
                      <div className="text-sm font-medium text-white truncate">
                        {storedTelegramUser.name}
                      </div>
                      <div className={`text-xs ${isTelegramExpired ? 'text-orange-400' : isTelegramUnverified ? 'text-amber-400/80' : 'text-zinc-500'}`}>
                        {isTelegramExpired ? 'Сессия истекла' : isTelegramUnverified ? 'Нет связи — данные из кэша' : 'Telegram'}
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="w-9 h-9 rounded-lg bg-[#27A7E7] flex items-center justify-center flex-shrink-0">
                      <svg className="w-5 h-5 text-on-accent" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
                      </svg>
                    </div>
                    <span className="ml-3 text-sm font-medium text-white">Telegram</span>
                  </>
                )}
                <svg className="ml-auto w-4 h-4 text-zinc-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
              
              {storedTelegramUser && !isTelegramExpired && (
                <button
                  onClick={handleClearTelegram}
                  className="flex absolute right-12 top-1/2 justify-center items-center w-7 h-7 rounded-lg transition-colors -translate-y-1/2 bg-zinc-800 hover:bg-red-500/20"
                  title="Выйти"
                >
                  <svg className="w-3.5 h-3.5 text-zinc-500 hover:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          )}

          {/* MAX Button — вход по коду из бота, как у Telegram */}
          {shouldShowMax && (
            <div className="relative">
              <button
                onClick={handleMaxClick}
                className={`w-full flex items-center px-4 py-3.5 rounded-xl transition-all duration-200 ${
                  isMaxExpired
                    ? 'bg-orange-500/10 border border-orange-500/30 hover:bg-orange-500/20'
                    : 'bg-[#5B5CFF]/10 border border-[#5B5CFF]/30 hover:bg-[#5B5CFF]/20'
                }`}
              >
                {storedMaxUser ? (
                  <>
                    <div className="overflow-hidden flex-shrink-0 w-9 h-9 rounded-lg bg-zinc-800">
                      {storedMaxUser.avatar ? (
                        <img src={storedMaxUser.avatar} alt="" className="object-cover w-full h-full" />
                      ) : (
                        <div className="flex justify-center items-center w-full h-full font-medium text-zinc-400">
                          {storedMaxUser.name.charAt(0)}
                        </div>
                      )}
                    </div>
                    <div className="flex-1 ml-3 min-w-0 text-left">
                      <div className="text-sm font-medium text-white truncate">
                        {storedMaxUser.name}
                      </div>
                      <div className={`text-xs ${isMaxExpired ? 'text-orange-400' : isMaxUnverified ? 'text-amber-400/80' : 'text-zinc-500'}`}>
                        {isMaxExpired ? 'Сессия истекла' : isMaxUnverified ? 'Нет связи — данные из кэша' : 'MAX'}
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <MaxIcon size="sm" />
                    <span className="ml-3 text-sm font-medium text-white">MAX</span>
                  </>
                )}
                <svg className="ml-auto w-4 h-4 text-zinc-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>

              {storedMaxUser && !isMaxExpired && (
                <button
                  onClick={handleClearMax}
                  className="flex absolute right-12 top-1/2 justify-center items-center w-7 h-7 rounded-lg transition-colors -translate-y-1/2 bg-zinc-800 hover:bg-red-500/20"
                  title="Выйти"
                >
                  <svg className="w-3.5 h-3.5 text-zinc-500 hover:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          )}

          {/* Yandex Button */}
          {shouldShowYandex && (
            <div className="relative">
              <button
                onClick={handleYandexClick}
                className={`w-full flex items-center px-4 py-3.5 rounded-xl transition-all duration-200 ${
                  isYandexExpired
                    ? 'bg-orange-500/10 border border-orange-500/30 hover:bg-orange-500/20'
                    : 'bg-[#FC3F1D]/10 border border-[#FC3F1D]/30 hover:bg-[#FC3F1D]/20'
                }`}
              >
                {storedYandexUser ? (
                  <>
                    <div className="overflow-hidden flex-shrink-0 w-9 h-9 rounded-lg bg-zinc-800">
                      {storedYandexUser.avatar ? (
                        <img src={storedYandexUser.avatar} alt="" className="object-cover w-full h-full" />
                      ) : (
                        <div className="flex justify-center items-center w-full h-full font-medium text-zinc-400">
                          {storedYandexUser.name.charAt(0)}
                        </div>
                      )}
                    </div>
                    <div className="flex-1 ml-3 min-w-0 text-left">
                      <div className="text-sm font-medium text-white truncate">
                        {storedYandexUser.name}
                      </div>
                      <div className={`text-xs ${isYandexExpired ? 'text-orange-400' : isYandexUnverified ? 'text-amber-400/80' : 'text-zinc-500'}`}>
                        {isYandexExpired ? 'Сессия истекла' : isYandexUnverified ? 'Нет связи — данные из кэша' : 'Яндекс ID'}
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="w-9 h-9 rounded-lg bg-[#FC3F1D] flex items-center justify-center flex-shrink-0">
                      <span className="text-base font-bold leading-none text-on-accent">Я</span>
                    </div>
                    <span className="ml-3 text-sm font-medium text-white">Яндекс ID</span>
                  </>
                )}
                <svg className="ml-auto w-4 h-4 text-zinc-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>

              {storedYandexUser && !isYandexExpired && (
                <button
                  onClick={handleClearYandex}
                  className="flex absolute right-12 top-1/2 justify-center items-center w-7 h-7 rounded-lg transition-colors -translate-y-1/2 bg-zinc-800 hover:bg-red-500/20"
                  title="Выйти"
                >
                  <svg className="w-3.5 h-3.5 text-zinc-500 hover:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          )}

          {/* Email Button */}
          {shouldShowEmail && (
            <div className="relative">
              <button
                onClick={handleEmailClick}
                className={`w-full flex items-center px-4 py-3.5 rounded-xl transition-all duration-200 ${
                  isEmailExpired
                    ? 'bg-orange-500/10 border border-orange-500/30 hover:bg-orange-500/20'
                    : 'bg-[#8B5CF6]/10 border border-[#8B5CF6]/30 hover:bg-[#8B5CF6]/20'
                }`}
              >
                {storedEmailUser ? (
                  <>
                    <div className="overflow-hidden flex-shrink-0 w-9 h-9 rounded-lg bg-zinc-800">
                      {storedEmailUser.avatar ? (
                        <img src={storedEmailUser.avatar} alt="" className="object-cover w-full h-full" />
                      ) : (
                        <div className="flex justify-center items-center w-full h-full font-medium text-zinc-400">
                          {storedEmailUser.name.charAt(0).toUpperCase()}
                        </div>
                      )}
                    </div>
                    <div className="flex-1 ml-3 min-w-0 text-left">
                      <div className="text-sm font-medium text-white truncate">
                        {storedEmailUser.name}
                      </div>
                      <div className={`text-xs ${isEmailExpired ? 'text-orange-400' : isEmailUnverified ? 'text-amber-400/80' : 'text-zinc-500'}`}>
                        {isEmailExpired ? 'Сессия истекла' : isEmailUnverified ? 'Нет связи — данные из кэша' : 'Почта'}
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="w-9 h-9 rounded-lg bg-[#8B5CF6] flex items-center justify-center flex-shrink-0">
                      <svg className="w-5 h-5 text-on-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <span className="ml-3 text-sm font-medium text-white">Почта</span>
                  </>
                )}
                <svg className="ml-auto w-4 h-4 text-zinc-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>

              {storedEmailUser && !isEmailExpired && (
                <button
                  onClick={handleClearEmail}
                  className="flex absolute right-12 top-1/2 justify-center items-center w-7 h-7 rounded-lg transition-colors -translate-y-1/2 bg-zinc-800 hover:bg-red-500/20"
                  title="Выйти"
                >
                  <svg className="w-3.5 h-3.5 text-zinc-500 hover:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          )}

          {/* Сохранённая почта занимает кнопку быстрым входом — вход другим адресом
              (и Яндекс ID) остаётся доступен отдельной строкой. */}
          {shouldShowEmail && storedEmailUser && !isEmailExpired && (
            <button
              onClick={onSelectEmail}
              className="w-full flex items-center justify-center px-4 py-3 rounded-xl border border-dashed border-[#8B5CF6]/40 text-[#8B5CF6] text-sm font-medium hover:bg-[#8B5CF6]/10 transition-colors"
            >
              <svg className="mr-2 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Другая почта или Яндекс ID
            </button>
          )}

        </div>

        {/* Connect accounts section */}
        {onConnectTelegram && shouldShowTelegram && !hasUnifiedAccount && storedVKUser && !storedTelegramUser && !isVKExpired && (
          <div className="p-4 mt-6 rounded-xl border bg-zinc-900 border-zinc-800">
            <p className="mb-3 text-xs text-center text-zinc-400">
              Подключите Telegram для быстрого входа
            </p>
            <button
              onClick={onConnectTelegram}
              className="w-full flex items-center justify-center px-4 py-2.5 rounded-lg bg-[#27A7E7]/10 border border-[#27A7E7]/30 text-[#27A7E7] text-sm font-medium hover:bg-[#27A7E7]/20 transition-colors"
            >
              <svg className="mr-2 w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
              </svg>
              Подключить Telegram
            </button>
          </div>
        )}

        {/* Error message */}
        {state.error && (
          <div className="p-3 mt-4 rounded-xl border bg-red-500/10 border-red-500/30">
            <p className="text-sm text-center text-red-400">{state.error}</p>
          </div>
        )}

        {/* Admin Panel */}
        {isAdmin && <AdminPanel isVisible={isAdmin} />}
      </div>

      {/* Footer */}
      <div className="px-6 py-4 border-t border-zinc-800/50">
        {skin ? (
          <SkinLegal skin={skin} />
        ) : (
          <p className="text-xs text-center text-zinc-600">
            Продолжая, вы соглашаетесь с условиями использования
          </p>
        )}
      </div>
    </div>
  );
};

export default AuthSelector;
